/**
 * The daily burn: how many calories you use in a day, worked out one of three ways (the user
 * picks in Targets & Profile, stored in diet_settings.energy_mode):
 *
 *  - activity: resting energy (BMR) x an activity multiplier (FAO/WHO/UNU physical activity levels,
 *    see nutrition.ts). The multiplier already includes exercise and digestion.
 *  - wearable: BMR + active calories from a watch (Health Connect via the phone app, or typed in)
 *    + walking no app counted + the thermic effect of the food logged that day. BMR is measured
 *    fasting and at rest, a watch's active calories exclude resting energy, and the walking part
 *    only prices steps taken while no app recorded active calories (steps_uncovered), so the
 *    parts don't overlap. With neither active calories nor such steps, a watch total (its own
 *    resting estimate + active) is used as is.
 *  - adaptive: learned from the weight trend against logged intake (trajectory engine); the
 *    activity estimate until there is enough data.
 *
 * Rules that must match the phone app's Kotlin copy (android/core/engine, pinned by
 * golden/energy.json): bmrOptions/chooseBmr, PAL, kcalPerStep, wearableBurn, tef.ts tefOfMeals.
 */
import type { BodyComp } from './bodyComp';
import { addDays, ageOn } from './dates';
import { cunninghamBmr, mifflinStJeorBmr, PAL } from './nutrition';
import { MIXED_DIET_TEF_SHARE, tefOfMeals, type TefSettings } from './tef';
import type { ActivityDay, ActivityLevel, BmrMode, DailyMetric, DietSettings, EnergyMode, Profile, Sex } from './types';

export const ENERGY_MODE_LABEL: Readonly<Record<EnergyMode, string>> = {
  activity: 'Standard activity level',
  wearable: 'BMR + my wearable',
  adaptive: 'Learn from my weight',
};

export const ENERGY_MODE_HELP: Readonly<Record<EnergyMode, string>> = {
  activity: 'Your resting burn (BMR) times a standard activity multiplier. Simple and steady, but it guesses how much you move.',
  wearable: 'Your resting burn (BMR) + all the active energy your watch and phone recorded, with no activity multiplier. The Mighty Peanut phone app reads it from Health Connect every time you open it. Steps taken while no app recorded active calories are added as walking. Digestion (TEF) is optional below. Watch calorie estimates can be off by 20-30% (Fuller 2020; Germini 2022), so compare with Learn from my weight after a few weeks.',
  adaptive: 'Worked out from how your weight trend moved against what you logged. Needs about 2 weeks of weigh-ins and food; uses your activity level until then.',
};

export const BMR_LABEL: Readonly<Record<BmrMode, string>> = {
  mifflin: 'Mifflin–St Jeor (standard)',
  lean: 'Cunningham (uses lean mass)',
  custom: 'My own number',
};

export interface BmrOptions {
  mifflin: number | null;
  lean: number | null;
  custom: number | null;
  /** Why an equation can't run yet (null when it can). */
  mifflinMissing: string | null;
  leanMissing: string | null;
}

export interface BmrChoice {
  kcal: number;
  method: BmrMode;
  /** Set when the chosen method couldn't run and another one was used. */
  wanted: BmrMode | null;
}

export function bmrOptions(profile: Pick<Profile, 'height_cm' | 'birth_date' | 'sex'>, comp: Pick<BodyComp, 'weightKg' | 'leanMassKg'>, today: string, customKcal: number | null): BmrOptions {
  const missing: string[] = [];
  if (comp.weightKg === null) missing.push('a weigh-in');
  if (profile.height_cm === null) missing.push('your height');
  if (profile.birth_date === null) missing.push('your birth date');
  if (profile.sex === null) missing.push('your sex');
  const mifflin: number | null = missing.length === 0
    ? mifflinStJeorBmr(comp.weightKg as number, profile.height_cm as number, ageOn(profile.birth_date as string, today), profile.sex!)
    : null;
  const lean: number | null = comp.leanMassKg !== null ? cunninghamBmr(comp.leanMassKg) : null;
  return {
    mifflin, lean, custom: customKcal,
    mifflinMissing: mifflin === null ? `Needs ${missing.join(', ')}.` : null,
    leanMissing: lean === null ? 'Needs a body-fat % or lean-mass measurement.' : null,
  };
}

const FALLBACK: Readonly<Record<BmrMode, readonly BmrMode[]>> = {
  mifflin: ['mifflin', 'lean', 'custom'],
  lean: ['lean', 'mifflin', 'custom'],
  custom: ['custom', 'mifflin', 'lean'],
};

/** The chosen BMR, or the next one that can run. */
export function chooseBmr(mode: BmrMode, o: BmrOptions): BmrChoice | null {
  for (const m of FALLBACK[mode]) {
    const v: number | null = o[m];
    if (v !== null && Number.isFinite(v) && v > 0) return { kcal: v, method: m, wanted: m === mode ? null : mode };
  }
  return null;
}

/** Multiplier for an activity level; no level set counts as sedentary. */
export const palFor = (level: ActivityLevel | null): number => PAL[level ?? 'sedentary'];

/**
 * Net cost of level walking: 0.1 mL O2 per kg per metre (the ACSM walking equation's horizontal
 * term, resting VO2 excluded) x ~5 kcal per litre of O2 = 0.5 kcal per kg per km.
 */
export const WALK_KCAL_PER_KG_KM: number = 0.5;

/** Step length as a share of height (Hoeger et al., ACSM Health Fit J 2008;12(1):14-19); the mean when sex is unknown. */
export const STEP_LENGTH_SHARE: Readonly<Record<Sex | 'unknown', number>> = { male: 0.415, female: 0.413, unknown: 0.414 };

/** Net calories per walking step for this body, or null without a weight and height. */
export function kcalPerStep(weightKg: number | null, heightCm: number | null, sex: Sex | null): number | null {
  if (weightKg === null || heightCm === null || !(weightKg > 0) || !(heightCm > 0)) return null;
  const stepKm: number = (heightCm / 100) * STEP_LENGTH_SHARE[sex ?? 'unknown'] / 1000;
  return WALK_KCAL_PER_KG_KM * weightKg * stepKm;
}

export interface WearableBurnInput {
  bmr: number | null;
  activeKcal: number | null;
  totalKcal: number | null;
  /** Walking no app counted (steps_uncovered x kcalPerStep). */
  walkingKcal: number | null;
  tefKcal: number | null;
  includeTef: boolean;
}

/**
 * BMR + active + walking (+ TEF) when there is a BMR and either movement part; else a watch
 * total (+ TEF).
 */
export function wearableBurn(i: WearableBurnInput): { kcal: number | null; basis: 'active' | 'total' | null } {
  const tef: number = i.includeTef ? i.tefKcal ?? 0 : 0;
  const walking: number = i.walkingKcal ?? 0;
  if (i.bmr !== null && (i.activeKcal !== null || walking > 0)) return { kcal: i.bmr + (i.activeKcal ?? 0) + walking + tef, basis: 'active' };
  if (i.totalKcal !== null) return { kcal: i.totalKcal + tef, basis: 'total' };
  return { kcal: null, basis: null };
}

/** A day's steps no app counted (never more than its steps), and their calories when the body is known. */
export function walkingOf(row: ActivityDay, perStep: number | null): { steps: number | null; kcal: number | null } {
  const u: number | null = row.steps_uncovered ?? null;
  const steps: number | null = u !== null && row.steps !== null ? Math.min(u, row.steps) : u;
  if (steps === null || steps <= 0) return { steps, kcal: null };
  return { steps, kcal: perStep === null ? null : steps * perStep };
}

/** One row per day: a typed-in (manual) row wins over the phone's Health Connect row. */
export function activityByDay(rows: ReadonlyArray<ActivityDay>): Map<string, ActivityDay> {
  const out = new Map<string, ActivityDay>();
  for (const r of rows) {
    const prev: ActivityDay | undefined = out.get(r.local_date);
    if (!prev || (r.source === 'manual' && prev.source !== 'manual')) out.set(r.local_date, r);
  }
  return out;
}

export interface Observed { kcal: number; low: number | null; high: number | null; status: string }

export interface EnergyContext {
  settings: DietSettings;
  bmr: BmrChoice | null;
  pal: number;
  /** kcalPerStep for the latest weight, height and sex. */
  kcalPerStep: number | null;
  rows: ReadonlyMap<string, ActivityDay>;
  observed: Observed | null;
  today: string;
}

export interface DayBurn {
  kcal: number | null;
  /** How the number was worked out (may differ from the chosen mode when it had to fall back). */
  method: 'wearable' | 'activity' | 'observed' | 'none';
  bmrKcal: number | null;
  activeKcal: number | null;
  totalKcal: number | null;
  /** Walking no app counted: its steps, and calories when they could be priced. */
  walkingSteps: number | null;
  walkingKcal: number | null;
  tefKcal: number | null;
  pal: number | null;
  basis: 'active' | 'total' | null;
  row: ActivityDay | null;
  /** Today, with the watch still counting. */
  partial: boolean;
  note: string | null;
}

function activityEstimate(ctx: EnergyContext, note: string | null): DayBurn {
  const kcal: number | null = ctx.bmr ? ctx.bmr.kcal * ctx.pal : null;
  return {
    kcal, method: kcal === null ? 'none' : 'activity', bmrKcal: ctx.bmr?.kcal ?? null, activeKcal: null, totalKcal: null,
    walkingSteps: null, walkingKcal: null, tefKcal: null, pal: ctx.pal, basis: null, row: null, partial: false,
    note: kcal === null ? 'Add your height, birth date, sex and a weigh-in (or your own BMR) to estimate your burn.' : note,
  };
}

/** The burn for one day. `tefKcal` is the thermic effect of that day's logged food (tefOfMeals). */
export function dayBurn(ctx: EnergyContext, day: string, tefKcal: number | null): DayBurn {
  const mode: EnergyMode = ctx.settings.energy_mode;
  if (mode === 'wearable') {
    const row: ActivityDay | null = ctx.rows.get(day) ?? null;
    if (row) {
      const walk = walkingOf(row, ctx.kcalPerStep);
      const wb = wearableBurn({ bmr: ctx.bmr?.kcal ?? null, activeKcal: row.active_kcal, totalKcal: row.total_kcal, walkingKcal: walk.kcal, tefKcal, includeTef: ctx.settings.include_tef });
      if (wb.kcal !== null) {
        const active: boolean = wb.basis === 'active';
        return {
          kcal: wb.kcal, method: 'wearable', bmrKcal: active ? ctx.bmr?.kcal ?? null : null,
          activeKcal: row.active_kcal, totalKcal: row.total_kcal,
          walkingSteps: active ? walk.steps : null, walkingKcal: active ? walk.kcal : null,
          tefKcal: ctx.settings.include_tef ? tefKcal : null,
          pal: null, basis: wb.basis, row, partial: day === ctx.today && !row.day_complete,
          note: (walk.steps ?? 0) > 0 && walk.kcal === null ? 'Add your height and a weigh-in to count the walking no app recorded.' : null,
        };
      }
      return activityEstimate(ctx, row.active_kcal !== null || (walk.steps ?? 0) > 0
        ? 'Your watch sent movement, but a resting burn is needed to add it to. Add your height, birth date, sex and a weigh-in, or your own BMR.'
        : 'Your watch sent no calories for this day, so your activity level is used.');
    }
    return activityEstimate(ctx, day > ctx.today ? null : 'No watch data for this day yet, so your activity level is used.');
  }
  if (mode === 'adaptive') {
    if (ctx.observed) {
      return {
        kcal: ctx.observed.kcal, method: 'observed', bmrKcal: ctx.bmr?.kcal ?? null, activeKcal: null, totalKcal: null,
        walkingSteps: null, walkingKcal: null, tefKcal: null, pal: null, basis: null, row: null, partial: false, note: null,
      };
    }
    return activityEstimate(ctx, 'Not enough weigh-ins and food logs yet to learn your burn, so your activity level is used.');
  }
  return activityEstimate(ctx, null);
}

export interface PlanningBurn {
  /** Typical daily burn for planning (goal intake, projections). */
  kcal: number | null;
  method: 'wearable' | 'activity' | 'observed' | 'none';
  /** Days of watch data behind a wearable figure. */
  days: number;
  low: number | null;
  high: number | null;
  note: string | null;
}

export const PLANNING_WINDOW_DAYS: number = 14;
export const MIN_WEARABLE_DAYS: number = 3;

/**
 * Typical daily burn: the chosen mode averaged over recent complete days. Wearable: BMR + active
 * + walking (or the watch total) on each of the last 14 finished days with watch data, plus the average
 * thermic effect of the days with food logged (or the mixed-diet 10% when none were logged).
 */
export function planningBurn(ctx: EnergyContext, metrics: ReadonlyArray<DailyMetric>, tef: TefSettings, ageYears: number | null): PlanningBurn {
  const activity = (note: string | null): PlanningBurn => {
    const e: DayBurn = activityEstimate(ctx, note);
    return { kcal: e.kcal, method: e.method === 'activity' ? 'activity' : 'none', days: 0, low: null, high: null, note: e.note };
  };
  const mode: EnergyMode = ctx.settings.energy_mode;
  if (mode === 'adaptive') {
    if (ctx.observed) return { kcal: ctx.observed.kcal, method: 'observed', days: 0, low: ctx.observed.low, high: ctx.observed.high, note: null };
    return activity('Not enough weigh-ins and food logs yet to learn your burn, so your activity level is used.');
  }
  if (mode === 'activity') return activity(null);

  const from: string = addDays(ctx.today, -PLANNING_WINDOW_DAYS);
  const byDay = new Map<string, DailyMetric>(metrics.map((m) => [m.day, m]));
  const bases: number[] = [];
  const tefs: number[] = [];
  for (const [day, row] of ctx.rows) {
    if (day < from || day >= ctx.today) continue;
    const walkingKcal: number | null = walkingOf(row, ctx.kcalPerStep).kcal;
    const base = wearableBurn({ bmr: ctx.bmr?.kcal ?? null, activeKcal: row.active_kcal, totalKcal: row.total_kcal, walkingKcal, tefKcal: 0, includeTef: false });
    if (base.kcal === null) continue;
    bases.push(base.kcal);
    const m: DailyMetric | undefined = byDay.get(day);
    if (m && m.meal_count > 0) {
      tefs.push(tefOfMeals([{ calories: m.calories, protein_g: m.protein_g, carbs_g: m.carbs_g, fat_g: m.fat_g, fiber_g: m.fiber_g, alcohol_g: m.alcohol_g, sugar_alcohols_g: null }], tef, ageYears).kcal);
    }
  }
  if (bases.length < MIN_WEARABLE_DAYS) {
    return activity(`Planning needs ${MIN_WEARABLE_DAYS}+ finished days of watch data (have ${bases.length}), so your activity level is used until then.`);
  }
  const mean = (xs: number[]): number => xs.reduce((a, x) => a + x, 0) / xs.length;
  const base: number = mean(bases);
  let tefPart: number = 0;
  let note: string | null = null;
  if (ctx.settings.include_tef) {
    if (tefs.length > 0) tefPart = mean(tefs);
    else {
      // Intake ~ burn, and digestion ~10% of intake: burn = base / (1 - 0.10).
      tefPart = (base * MIXED_DIET_TEF_SHARE) / (1 - MIXED_DIET_TEF_SHARE);
      note = 'No food logged on those days, so digestion is estimated at 10% of intake.';
    }
  }
  const lo: number = Math.min(...bases) + tefPart;
  const hi: number = Math.max(...bases) + tefPart;
  return { kcal: base + tefPart, method: 'wearable', days: bases.length, low: lo, high: hi, note };
}
